/**
 * Helpers for shaping a RISA-Assist (`extension_session`) audit trail into the
 * blocks rendered by the session viewer.
 */

import { ExtensionEntryKind } from "../../../../enums/eventType";
import { AssistSessionEntry } from "./assistSessionViewer.types";

/**
 * A question together with the browser actions it triggered.
 *
 * Actions recorded before the first question have no `question` and are
 * rendered on their own.
 */
export interface AssistSessionGroup {
  question?: AssistSessionEntry;
  actions: AssistSessionEntry[];
}

/**
 * Groups a chronological session into question-led blocks.
 *
 * Every action entry is attached to the most recent preceding question, since
 * the assistant captures its screenshots while answering it.
 *
 * @param entries - Session entries in chronological order
 * @returns The grouped blocks in the same order
 */
export const groupSessionEntries = (
  entries: AssistSessionEntry[],
): AssistSessionGroup[] => {
  const groups: AssistSessionGroup[] = [];

  entries.forEach((entry) => {
    const isQuestion = entry?.metadata?.kind === ExtensionEntryKind.QUESTION;
    const currentGroup = groups[groups.length - 1];

    if (isQuestion || !currentGroup) {
      groups.push({
        question: isQuestion ? entry : undefined,
        actions: isQuestion ? [] : [entry],
      });
      return;
    }
    currentGroup.actions.push(entry);
  });

  return groups;
};

/**
 * Builds the single-line label shown for a browser action.
 *
 * @param actionType - Already humanised action type, e.g. "Screenshot"
 * @param tab - Tab the action ran on, when recorded
 * @param actionValue - Value the action carried, when recorded
 * @returns A human readable description of the action
 */
export const buildActionLabel = (
  actionType: string,
  tab?: string,
  actionValue?: string | null,
): string => {
  const suffixes = [tab, actionValue].filter(Boolean).join(" · ");
  return suffixes ? `${actionType} · ${suffixes}` : actionType;
};
